import { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { useMutate } from 'restful-react'
import { ClientType, UserContext, UserContextState, UserContextType } from '@/UserContext'
import GuestJoinForm from './internal/GuestJoinForm'
import { LaunchAppButton } from './internal/LaunchAppButton'
import { logger } from '@/services/Logger'
import { useTranslation } from 'react-i18next'
import { env } from '@/services/EnvironmentVariables'
import { getAppSerialId } from '@/actions/AppSerialId'
import { parseJoinRoomLink } from '@/services/JoinRoomLink'
import useMediaSelectionActions from '@/actions/useMediaSelectionActions'
import useMediaSourceActions from '@/functionalities/MediaSources/useMediaSourceActions'
import useJoinRoom from '@/services/useJoinRoom'
import { wsConnection } from '@/services/WSConnectionInstance'
import { sleep } from '@/utils'
import JoinSessionPasswordPrompt from '@/JoinSessionPasswordPrompt'
import { triggerPermissionPromptOnWebapp } from '@/services/MediaDevices'
import clsx from 'clsx'
import { useInIframe } from '@/services/useInIframe'
import UnauthorizedPageBase from '@/pages/UnauthorizedPages/UnauthorizedPageBase'
import { MuseLogo } from '@/components/MuseLogo'
import { LocalVideoTrack } from 'livekit-client'
import { Button } from '@/UIComponents2/Button'
import CameraIcon from '@/CustomIcons/CameraIcon'
import { cn } from '@/utlis/cn'
import useRoomActions from '@/services/useRoomActions'
import MicrophoneIcon from '@/CustomIcons/MicrophoneIcon'
import MicrophoneOffIcon from '@/CustomIcons/MicrophoneOffIcon'
import { VolumeMeter } from '@/components/VolumeMeter'
import useRootSnackbar from '@/services/useRootSnackbar'
import CrossPlatformDialog from '@/dialogs/components/CrossPlatformDialog'
import { LanguagesPopover } from '@/components/LanguagesPopover'
import { CircleIconButton } from '@/UIComponents2/CircleIconButton'
import CameraOffIcon from '@/CustomIcons/CameraOffIcon'
import LanguageIcon from '@/CustomIcons/LanguageIcon'
import { CameraPreview } from '@/components/CameraPreview'
import { OverlayControlButton } from '@/components/OverlayControlButton'

const log = logger.createChildLogger({ name: 'GuestJoinPage' })

/**
 * About JOIN_ROOM_LINK:
 *   If the join room link has query parameter `clientType=listenonly`, then the `code` parameter
 *   must be a 48-digit anonymousId, e.g. http://localhost:8080/guest-join?clientType=listenonly&code=6ad1...485f
 *
 *   We use the `clientType=listenonly` param to know that it is a listenonly session.
 *
 *   For listenonly join code, we must send
 *     `POST /v1/guests` with { roomJoinCode: "muse:joinuid:6ad1...485f" } (that is, the code appended by "muse:joinuid:")
 *   for the create-guest api route, and
 *     `GET /v1/rooms/muse:joinuid:${code}`
 *   for the get-room api route.
 *
 *   This lets the api search for the room only matching the code with the anonymousId, and prevents
 *   the user from attempting to use the anonymousId to join rooms that are NOT listen-only. If the user
 *   attempts to do so, then the api call is sent with the code being "muse:joincode:${code}", which fails,
 *   because the api is not expecting `code` to be an anonymousId if prepended by "muse:joincode:".
 */

export function GuestJoinPage() {
    const history = useHistory()
    const { state, dispatch } = useContext(UserContext) as UserContextType
    const isElectron = useMemo(() => !!env.IS_ELECTRON, [])
    const { t } = useTranslation()

    const [joinCode, setJoinCode] = useState<string | undefined>()
    const [joinLinkMetadata, setJoinLinkMetadata] = useState<Record<string, string> | undefined>()
    const [clientType, setClientType] = useState<ClientType | null>(null)
    const { chooseDefaultMediaSelection } = useMediaSelectionActions()
    const { loadAudioAndVideoMediaSources } = useMediaSourceActions()
    const [isPermissionsAlertOpen, setIsPermissionsAlertOpen] = useState(false)
    const [isPermissionsDenied, setIsPermissionsDenied] = useState(false)
    const [isBlockedModalOpen, setIsBlockedModalOpen] = useState(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const inIframe = useInIframe()
    const {
        joincode,
        joincodeMetadata,
        joinRoomWithCode,
        joinRoomLoading,
        joinSessionErrorMessage,
        showPasswordPrompt,
        closePasswordPrompt,
    } = useJoinRoom()
    const RootSnackbar = useRootSnackbar()
    const showErrorSnackbar = (errorMessage: string) => {
        return RootSnackbar.show({
            message: errorMessage,
            severity: 'error',
        })
    }

    const isViewer = clientType === 'listenonly'

    const { mutate: guestJoin } = useMutate({
        verb: 'POST',
        path: `/v1/guests`,
    })

    useEffect(() => {
        // If the user is already logged in with a real account, send them to
        // /choose-initial-mode which will trigger a room join since the code
        // is still set in the env.
        if (state.userUrn && !state.isGuest && !isViewer && !inIframe) {
            return
        }

        const { error, code, metadata, clientType } = parseJoinRoomLink(window.location.toString())

        setJoinCode(code)
        setJoinLinkMetadata(metadata)
        setClientType((clientType || '') as ClientType)
        if (error) {
            log.error('useEffect-extractRoomCode', 'Failed to parse join code', { error })
            history.replace('/login')
        }

        logger.event({ page: 'guest-join', action: 'enter' })
        return () => {
            logger.event({ page: 'guest-join', action: 'exit' })
        }
    }, [])

    useEffect(() => {
        if (inIframe === true) {
            window.localStorage.setItem('iframeJoinLink', window.location.href)
        }
    }, [inIframe])

    const loadMediaSources = useCallback(async () => {
        const hasPermissionPromise = triggerPermissionPromptOnWebapp()
        const timeoutPromise = new Promise((resolve) => setTimeout(resolve, 1500))

        Promise.race([hasPermissionPromise, timeoutPromise]).then((result) => {
            if (result === false) {
                setIsPermissionsAlertOpen(false)
                setIsPermissionsDenied(true)
                setIsBlockedModalOpen(true)
            } else if (result !== true) {
                setIsPermissionsAlertOpen(true)
            }
        })

        const hasPermission = await hasPermissionPromise
        setIsPermissionsAlertOpen(false)
        if (!hasPermission) {
            setIsPermissionsDenied(true)
        }

        await loadAudioAndVideoMediaSources()
    }, [])

    useEffect(() => {
        if (clientType == null) {
            return
        }

        if (!state.mediaSources.loaded) {
            if (!isViewer) {
                loadMediaSources()
            }
            return
        }

        if (!state.mediaSelection.isDefaultMediaSelectionLoaded) {
            if (!isViewer) {
                chooseDefaultMediaSelection(state.mediaSources)
            }
        }
    }, [state.mediaSources, state.mediaSelection.isDefaultMediaSelectionLoaded, clientType, loadMediaSources, isViewer])

    const joinHandler = async (formData: any) => {
        try {
            setIsLoading(true)

            let roomJoinCode = joinCode
            if (isViewer) {
                roomJoinCode = 'muse:joinuid:' + joinCode
            }

            const result = await guestJoin({ appSerialId: getAppSerialId(), displayName: formData.name, roomJoinCode })
            if (result.token) {
                await guestJoinSuccessHandler(result.token, result.user?._id)
            } else {
                showErrorSnackbar('Internal error: the sever did not return a token.')
            }

            setIsLoading(false)
        } catch (e) {
            setIsLoading(false)

            console.error('caught login error: %O', e)
            const response = (e as any) || {}
            if (!response?.data) {
                showErrorSnackbar(
                    "Couldn't connect to the server. Please make sure your are connected to the internet."
                )
            } else if (response.status === 404) {
                showErrorSnackbar('The session you are trying to join does not exist.')
            } else if (response.status === 403) {
                showErrorSnackbar('The session you are trying to join does not allow guests. Please create an account.')
            } else {
                showErrorSnackbar(`Unknown error: status=${(e as any)?.status} message=${(e as any)?.message}`)
            }
        }
    }

    const waitForWebsocketConnection = async () => {
        return new Promise((resolve, _reject) => {
            let timeout = setTimeout(() => {
                return resolve(false)
            }, 5000)

            wsConnection.addEventListener('onopen', () => {
                clearTimeout(timeout)
                return resolve(true)
            })
        })
    }

    const guestJoinSuccessHandler = async (authToken: string, userUrn: string) => {
        const shouldWaitForWsConnection = wsConnection.getSocket()?.readyState !== WebSocket.OPEN

        dispatch({
            type: 'guest_login',
            value: {
                userUrn,
                authToken,
                clientType: (clientType as ClientType) || '',
            },
        })

        // Wait for the login process to complete (especially for the websocket connection to be complete).
        if (shouldWaitForWsConnection) {
            await waitForWebsocketConnection()
        } else {
            // ugly: wait for the login dispatch to settle.
            await sleep(500)
        }

        if (joinCode) {
            // `joinRoomLoading` is set while loading the room.
            joinRoomWithCode(joinCode, undefined, joinLinkMetadata, undefined, clientType || '')
        }
    }

    useEffect(() => {
        if (joinSessionErrorMessage) {
            showErrorSnackbar(joinSessionErrorMessage)
        }
    }, [joinSessionErrorMessage])

    useEffect(() => {
        // @ts-ignore
        window.userState = state
    }, [state])

    return (
        <>
            <DesktopContainer
                joinCode={joinCode}
                joinLinkMetadata={joinLinkMetadata}
                clientType={clientType}
                setIsBlockedModalOpen={setIsBlockedModalOpen}
                joinHandler={joinHandler}
                isLoading={isLoading}
                joinRoomLoading={joinRoomLoading}
                showPasswordPrompt={showPasswordPrompt}
                isPermissionsDenied={isPermissionsDenied}
            />

            {!isViewer && (
                <MobileDrawerContainer
                    userState={state}
                    joinCode={joinCode}
                    joinLinkMetadata={joinLinkMetadata}
                    clientType={clientType}
                    setIsBlockedModalOpen={setIsBlockedModalOpen}
                    joinHandler={joinHandler}
                    isLoading={isLoading}
                    joinRoomLoading={joinRoomLoading}
                    showPasswordPrompt={showPasswordPrompt}
                    isPermissionsDenied={isPermissionsDenied}
                />
            )}

            {showPasswordPrompt && (
                <JoinSessionPasswordPrompt
                    onClose={(password: string) => {
                        if (password) {
                            joinRoomWithCode(joincode, password, joinLinkMetadata, undefined, clientType || '')
                        } else {
                            closePasswordPrompt()
                        }
                    }}
                />
            )}
            {!isElectron && (
                <>
                    <CrossPlatformDialog
                        onDefaultClose={() => setIsPermissionsAlertOpen(false)}
                        forceBrowserModal
                        show={isPermissionsAlertOpen}
                    >
                        <div className="flex flex-col items-center justify-center gap-4 p-10">
                            <div className="flex flex-col items-center justify-center gap-4">
                                <h2 className="text-center text-xl font-medium">{t('Click Allow')}</h2>
                                <p className="text-white text-center text-sm">
                                    {t('You can still turn your camera and microphone off later.')}
                                </p>
                            </div>
                            <Button onClick={() => setIsPermissionsAlertOpen(false)} variant="link">
                                {t('Not now')}
                            </Button>
                        </div>
                    </CrossPlatformDialog>
                    <CrossPlatformDialog
                        onDefaultClose={() => setIsBlockedModalOpen(false)}
                        forceBrowserModal
                        show={isBlockedModalOpen}
                    >
                        <div className="flex flex-col items-center justify-center gap-4 p-10">
                            <div className="flex flex-col items-center justify-center gap-4">
                                <h2 className="text-white text-center text-xl font-medium">{t('Muse is blocked')}</h2>
                                <p className="text-white text-center text-sm">
                                    {t(
                                        'You need to update your browser settings to allow camera and microphone access.'
                                    )}
                                </p>
                            </div>
                            <Button onClick={() => setIsPermissionsAlertOpen(false)} variant="link">
                                {t('Not now')}
                            </Button>
                        </div>
                    </CrossPlatformDialog>
                </>
            )}
        </>
    )
}

type DesktopContainerProps = {
    joinCode: string | undefined
    joinLinkMetadata: { [key: string]: string } | undefined
    clientType: string | null
    setIsBlockedModalOpen: (open: boolean) => void
    joinHandler: (formData: any) => void
    isLoading: boolean
    joinRoomLoading: boolean
    showPasswordPrompt: boolean
    isPermissionsDenied: boolean
}
function DesktopContainer(props: DesktopContainerProps) {
    const { t } = useTranslation()
    const isViewer = props.clientType === 'listenonly'

    return (
        <UnauthorizedPageBase
            className={cn(!isViewer && 'hidden md:flex')}
            contentClassName={cn(isViewer && 'w-[550px]')}
        >
            <div className="flex justify-between items-center">
                <div className={cn('w-[108px] h-[24px] flex-none')}>
                    <MuseLogo />
                </div>

                <LanguagesPopover>
                    <CircleIconButton>
                        <LanguageIcon />
                    </CircleIconButton>
                </LanguagesPopover>
            </div>

            <div className="h-[80%] flex flex-col justify-center">
                <span className="w-full text-[30px]/[50px] font-[500] bg-linear-[98deg,white_21.88%,transparent_139.38%] bg-clip-text text-transparent mt-[-20px]">
                    {isViewer ? t('Watch session') : t('Join session')}
                </span>
                <GuestJoinForm
                    onSubmit={props.joinHandler}
                    loading={props.isLoading || props.joinRoomLoading || props.showPasswordPrompt}
                    joinCode={props.joinCode ?? ''}
                    joinLinkMetadata={props.joinLinkMetadata ?? {}}
                    clientType={props.clientType}
                    isPermissionsDenied={props.isPermissionsDenied}
                    setIsBlockedModalOpen={props.setIsBlockedModalOpen}
                />
            </div>
            <div>
                {!isViewer && <LaunchAppButton joinCode={props.joinCode} joinLinkMetadata={props.joinLinkMetadata} />}
            </div>
        </UnauthorizedPageBase>
    )
}

type MobileDrawerContainerProps = {
    userState: UserContextState
    joinCode: string | undefined
    joinLinkMetadata: { [key: string]: string } | undefined
    clientType: string | null
    setIsBlockedModalOpen: (open: boolean) => void
    joinHandler: (formData: any) => void
    isLoading: boolean
    joinRoomLoading: boolean
    showPasswordPrompt: boolean
    isPermissionsDenied: boolean
}
function MobileDrawerContainer(props: MobileDrawerContainerProps) {
    const { t } = useTranslation()
    const { calcIsMyAudioDeviceAllMuted, toggleMyAudioDeviceMute, isMyVideoDeviceMuted, toggleMyVideoDeviceMute } =
        useRoomActions()

    let bgStyle =
        "bg-[url('/images/mobile-blur.webp')] bg-black bg-no-repeat bg-cover bg-local overflow-x-hidden overflow-y-auto"

    return (
        <div className={cn('w-full h-full flex flex-col gap-[24px] px-[16px] pt-6 md:hidden', bgStyle)}>
            <div className={cn('w-[108px] h-[24px] flex-none')}>
                <MuseLogo />
            </div>
            <div className="flex flex-col h-full min-h-0 gap-2 min-w-[250px] justify-between ">
                <div className={cn('flex-1 min-h-[300px] min-w-[160px]')}>
                    <div className="relative h-full max-w-full">
                        {!(
                            props.userState.mediaStreams.video instanceof LocalVideoTrack &&
                            props.userState.mediaStreams.video.mediaStream
                        ) && (
                            <div className="h-full rounded-3xl aspect-9/16 border-muse-toplit flex items-center justify-center p-4 text-[16px] text-center text-muse-fg bg-[#282828]">
                                {props.isPermissionsDenied
                                    ? t('Muse needs permissions to access your camera')
                                    : t('no video input')}
                            </div>
                        )}
                        <CameraPreview
                            videoTrack={props.userState.mediaStreams.video}
                            isPermissionsDenied={props.isPermissionsDenied}
                            aspectRatio="9/16"
                            className="w-full max-h-full object-cover"
                        />
                        <div className="absolute bottom-4 right-4 flex gap-2 h-[50px]">
                            <OverlayControlButton
                                className="h-full"
                                onClick={() => {
                                    if (props.isPermissionsDenied) {
                                        props.setIsBlockedModalOpen(true)
                                    } else {
                                        toggleMyVideoDeviceMute()
                                    }
                                }}
                            >
                                {isMyVideoDeviceMuted() || props.isPermissionsDenied ? (
                                    <CameraOffIcon className="text-muse-danger" />
                                ) : (
                                    <CameraIcon />
                                )}
                            </OverlayControlButton>
                            <OverlayControlButton
                                className="h-full apsect-auto px-3 gap-2"
                                onClick={() => {
                                    if (props.isPermissionsDenied) {
                                        props.setIsBlockedModalOpen(true)
                                    } else {
                                        toggleMyAudioDeviceMute()
                                    }
                                }}
                            >
                                <VolumeMeter
                                    stream={props.userState.mediaStreams.audioInputs[0]}
                                    channelCount={1}
                                    variant="pills"
                                />
                                {calcIsMyAudioDeviceAllMuted() || props.isPermissionsDenied ? (
                                    <MicrophoneOffIcon className="text-muse-danger ml-[-1px]" />
                                ) : (
                                    <MicrophoneIcon className="ml-[-1px]" />
                                )}
                            </OverlayControlButton>
                        </div>
                    </div>
                </div>
                <div
                    className={clsx(
                        'bg-[#202122] rounded-t-[15px] pb-[10px] pt-[20px] px-4 -mx-4',
                        'border-muse-toplit',
                        'shadow-glow',
                        'focus:outline-0',
                        'z-2'
                    )}
                >
                    <div className="flex flex-col items-center justify-center gap-4">
                        <GuestJoinForm
                            onSubmit={props.joinHandler}
                            loading={props.isLoading || props.joinRoomLoading || props.showPasswordPrompt}
                            joinCode={props.joinCode ?? ''}
                            joinLinkMetadata={props.joinLinkMetadata ?? {}}
                            clientType={props.clientType}
                            isPermissionsDenied={props.isPermissionsDenied}
                            setIsBlockedModalOpen={props.setIsBlockedModalOpen}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
